import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, AlertTriangle, ChevronDown, ChevronRight,
  ShieldAlert, Settings2, Scale,
} from 'lucide-react';
import {
  VALID_OPERATORS, VALID_VALUE_TYPES, VALID_SIGNAL_TYPES, VALID_POLICIES, VALID_LABELS,
  OPERATOR_LABELS, SIGNAL_TYPE_LABELS, POLICY_LABELS,
  OPERATOR_TYPE_COMPAT,
  validateScoringConfig,
} from '../lib/qualification';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uid = () => Math.random().toString(36).slice(2, 10);

const DEFAULT_RULE = () => ({
  id: uid(), field_id: '', operator: 'contains', value: '', value_type: 'string',
  points: 10, label: '', signal_type: 'custom', hard_disqualify: false,
  required_for_finalization: false, confirmation_policy_override: null,
  expires_after_seconds: null,
});

const DEFAULT_CONFLICT_CONDITION = () => ({
  field_id: '', operator: 'equals', value: '', value_type: 'string',
});

const DEFAULT_CONFLICT_RULE = () => ({
  id: uid(), description: '', conditions: [DEFAULT_CONFLICT_CONDITION()],
  action: 'cap_label', cap_value: 'warm',
});

export const LeadScoringEditor = ({ qrId, onClose }) => {
  const { token } = useAuth();

  // Config state
  const [enabled, setEnabled] = useState(false);
  const [rules, setRules] = useState([]);
  const [conflictRules, setConflictRules] = useState([]);
  const [hotThreshold, setHotThreshold] = useState(70);
  const [warmThreshold, setWarmThreshold] = useState(40);
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);
  const [confirmationPolicy, setConfirmationPolicy] = useState('strict');
  const [version, setVersion] = useState(0);

  // UI state
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formFields, setFormFields] = useState([]);
  const [expandedSections, setExpandedSections] = useState({ scoring: true, disqualification: true, confirmation: true, conflicts: false });

  useEffect(() => { fetchConfig(); fetchFormFields(); }, [fetchConfig, fetchFormFields]);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/qr-codes/${qrId}/lead-scoring`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const c = res.data || {};
      setEnabled(c.enabled ?? false);
      setHotThreshold(c.hot_threshold ?? 70);
      setWarmThreshold(c.warm_threshold ?? 40);
      setMinScore(c.min_score ?? 0);
      setMaxScore(c.max_score ?? 100);
      setConfirmationPolicy(c.confirmation_policy ?? 'strict');
      setVersion(c.version ?? 0);
      setConflictRules(c.conflict_rules ?? []);
      setRules((c.rules || []).map(r => ({
        id: r.id || uid(),
        field_id: r.field_id || '',
        operator: r.operator || 'contains',
        value: r.value ?? '',
        value_type: r.value_type || 'string',
        points: r.points ?? 0,
        label: r.label || '',
        signal_type: r.signal_type || 'custom',
        hard_disqualify: r.hard_disqualify ?? false,
        required_for_finalization: r.required_for_finalization ?? false,
        confirmation_policy_override: r.confirmation_policy_override ?? null,
        expires_after_seconds: r.expires_after_seconds ?? null,
      })));
    } catch (e) {
      console.error('[LeadScoring] Failed to fetch config:', e);
    } finally {
      setLoading(false);
    }
  }, [qrId, token]);

  const fetchFormFields = useCallback(async () => {
    try {
      const subsRes = await axios.get(`${API}/qr-codes/${qrId}/submissions-detail`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const subs = subsRes.data.submissions || subsRes.data || [];
      if (subs.length > 0) {
        const responses = subs[0].responses || {};
        setFormFields(Object.keys(responses).map(k => ({ id: k, text: k })));
      }
    } catch {} // Non-blocking
  }, [qrId, token]);

  // ─── Validation ──
  const buildPayload = useCallback(() => ({
    enabled,
    rules: rules.map(r => ({
      id: r.id, field_id: r.field_id, operator: r.operator, value: r.value,
      value_type: r.value_type, points: Number(r.points) || 0, label: r.label,
      signal_type: r.signal_type, hard_disqualify: r.hard_disqualify,
      required_for_finalization: r.required_for_finalization,
      confirmation_policy_override: r.confirmation_policy_override || null,
      expires_after_seconds: r.expires_after_seconds ? Number(r.expires_after_seconds) : null,
    })),
    conflict_rules: conflictRules.map(cr => ({
      id: cr.id, description: cr.description || '',
      conditions: (cr.conditions || []).map(c => ({
        field_id: c.field_id, operator: c.operator, value: c.value, value_type: c.value_type || 'string',
      })),
      action: 'cap_label', cap_value: cr.cap_value,
    })),
    hot_threshold: Number(hotThreshold) || 70,
    warm_threshold: Number(warmThreshold) || 40,
    min_score: Number(minScore) || 0,
    max_score: Number(maxScore) || 100,
    confirmation_policy: confirmationPolicy,
  }), [enabled, rules, conflictRules, hotThreshold, warmThreshold, minScore, maxScore, confirmationPolicy]);

  const validation = useMemo(() => validateScoringConfig(buildPayload()), [buildPayload]);
  const hasBlockingErrors = validation.blocking.length > 0;

  // ─── Rule CRUD ──
  const addRule = () => setRules(prev => [...prev, DEFAULT_RULE()]);
  const removeRule = (i) => setRules(prev => prev.filter((_, idx) => idx !== i));
  const updateRule = (i, field, value) => setRules(prev => {
    const updated = [...prev];
    updated[i] = { ...updated[i], [field]: value };
    // Auto-fix operator when value_type changes
    if (field === 'value_type') {
      const compat = OPERATOR_TYPE_COMPAT[updated[i].operator];
      if (compat && !compat.includes(value)) {
        updated[i].operator = value === 'number' ? 'equals' : 'contains';
      }
    }
    return updated;
  });

  // ─── Conflict Rule CRUD ──
  const addConflictRule = () => setConflictRules(prev => [...prev, DEFAULT_CONFLICT_RULE()]);
  const removeConflictRule = (i) => setConflictRules(prev => prev.filter((_, idx) => idx !== i));
  const updateConflictRule = (i, field, value) => setConflictRules(prev => {
    const updated = [...prev];
    updated[i] = { ...updated[i], [field]: value };
    return updated;
  });
  const addCondition = (crIdx) => setConflictRules(prev => {
    const updated = [...prev];
    updated[crIdx] = { ...updated[crIdx], conditions: [...updated[crIdx].conditions, DEFAULT_CONFLICT_CONDITION()] };
    return updated;
  });
  const removeCondition = (crIdx, condIdx) => setConflictRules(prev => {
    const updated = [...prev];
    updated[crIdx] = { ...updated[crIdx], conditions: updated[crIdx].conditions.filter((_, j) => j !== condIdx) };
    return updated;
  });
  const updateCondition = (crIdx, condIdx, field, value) => setConflictRules(prev => {
    const updated = [...prev];
    const conds = [...updated[crIdx].conditions];
    conds[condIdx] = { ...conds[condIdx], [field]: value };
    updated[crIdx] = { ...updated[crIdx], conditions: conds };
    return updated;
  });

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ─── Save ──
  const handleSave = async () => {
    if (hasBlockingErrors) {
      console.error('[LeadScoring] Blocked submission:', validation.blocking);
      toast.error('Fix validation errors before saving.');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await axios.put(`${API}/qr-codes/${qrId}/lead-scoring`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVersion(res.data?.config?.version ?? version + 1);
      toast.success('Lead scoring saved!');
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.details) {
        console.error('[LeadScoring] Validation errors from server:', detail.details);
        toast.error(`${detail.message}: ${detail.details[0]}`);
      } else {
        console.error('[LeadScoring] Save failed:', e);
        toast.error(typeof detail === 'string' ? detail : 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8" role="status" data-testid="lead-scoring-loading">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1d4ed8]" aria-hidden="true" />
        <span className="ml-2 text-sm text-[hsl(var(--muted-foreground))]">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="lead-scoring-editor">
      {/* Enable toggle + version */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Switch
            id="scoring-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="scoring-enabled-toggle"
            aria-label="Enable lead scoring"
          />
          <Label htmlFor="scoring-enabled" className="font-semibold cursor-pointer">
            Enable Lead Scoring
          </Label>
        </div>
        <span className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="config-version-display">
          Config v{version}
        </span>
      </div>

      {enabled && (
        <>
          {/* ── Section: Configuration ── */}
          <SectionHeader
            title="Configuration"
            icon={Settings2}
            expanded={expandedSections.scoring}
            onToggle={() => toggleSection('scoring')}
            testId="section-scoring"
          />
          {expandedSections.scoring && (
            <div className="space-y-3 pl-1">
              <div className="grid grid-cols-2 gap-3">
                <NumberField id="hot-threshold" label="Hot threshold (score >=)" value={hotThreshold} onChange={setHotThreshold} testId="hot-threshold-input" accent="text-red-500" />
                <NumberField id="warm-threshold" label="Warm threshold (score >=)" value={warmThreshold} onChange={setWarmThreshold} testId="warm-threshold-input" accent="text-amber-500" />
                <NumberField id="min-score" label="Min score" value={minScore} onChange={setMinScore} testId="min-score-input" />
                <NumberField id="max-score" label="Max score" value={maxScore} onChange={setMaxScore} testId="max-score-input" />
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Scores below {warmThreshold} = <span className="text-blue-500 font-medium">Cold</span>
              </p>

              {/* Confirmation Policy */}
              <div>
                <Label htmlFor="confirmation-policy" className="text-xs font-medium">Confirmation Policy</Label>
                <select
                  id="confirmation-policy"
                  value={confirmationPolicy}
                  onChange={(e) => setConfirmationPolicy(e.target.value)}
                  className="w-full mt-1 px-2 py-2 border border-[hsl(var(--border))] rounded-md text-sm bg-[hsl(var(--background))]"
                  data-testid="confirmation-policy-select"
                  aria-describedby="confirmation-policy-desc"
                >
                  {VALID_POLICIES.map(p => <option key={p} value={p}>{POLICY_LABELS[p]}</option>)}
                </select>
                <p id="confirmation-policy-desc" className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                  Controls how inferred answers contribute to scoring
                </p>
              </div>
            </div>
          )}

          {/* ── Section: Scoring Rules ── */}
          <SectionHeader
            title={`Scoring Rules (${rules.filter(r => !r.hard_disqualify).length})`}
            icon={Scale}
            expanded={expandedSections.disqualification}
            onToggle={() => toggleSection('disqualification')}
            testId="section-rules"
          />
          {expandedSections.disqualification && (
            <div className="space-y-3 pl-1">
              <Button size="sm" variant="outline" onClick={addRule} className="rounded-full" data-testid="add-rule-btn">
                <Plus className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Add Rule
              </Button>
              {rules.length === 0 && (
                <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-3">No rules yet.</p>
              )}
              {rules.map((rule, i) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  index={i}
                  formFields={formFields}
                  confirmationPolicy={confirmationPolicy}
                  onUpdate={updateRule}
                  onRemove={removeRule}
                />
              ))}
            </div>
          )}

          {/* ── Section: Conflict Rules ── */}
          <SectionHeader
            title={`Conflict Rules (${conflictRules.length})`}
            icon={ShieldAlert}
            expanded={expandedSections.conflicts}
            onToggle={() => toggleSection('conflicts')}
            testId="section-conflicts"
          />
          {expandedSections.conflicts && (
            <div className="space-y-3 pl-1">
              <Button size="sm" variant="outline" onClick={addConflictRule} className="rounded-full" data-testid="add-conflict-rule-btn">
                <Plus className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Add Conflict Rule
              </Button>
              {conflictRules.length === 0 && (
                <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-3">
                  Conflict rules cap the qualification label when specific conditions are met.
                </p>
              )}
              {conflictRules.map((cr, i) => (
                <ConflictRuleCard
                  key={cr.id}
                  rule={cr}
                  index={i}
                  formFields={formFields}
                  onUpdate={updateConflictRule}
                  onRemove={removeConflictRule}
                  onAddCondition={addCondition}
                  onRemoveCondition={removeCondition}
                  onUpdateCondition={updateCondition}
                />
              ))}
            </div>
          )}

          {/* ── Validation Messages ── */}
          {(validation.blocking.length > 0 || validation.warnings.length > 0) && (
            <div className="space-y-2" data-testid="validation-messages" role="alert" aria-live="polite">
              {validation.blocking.map((err, i) => (
                <div key={`b-${i}`} className="flex items-start gap-2 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-800">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{err}</span>
                </div>
              ))}
              {validation.warnings.map((warn, i) => (
                <div key={`w-${i}`} className="flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{warn}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Actions ── */}
      <div className="flex gap-3 pt-2 border-t border-[hsl(var(--border))]">
        <Button
          onClick={handleSave}
          disabled={saving || (enabled && hasBlockingErrors)}
          className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] flex-1"
          data-testid="save-scoring-btn"
          aria-disabled={saving || (enabled && hasBlockingErrors)}
        >
          <Save className="h-4 w-4 mr-2" aria-hidden="true" />
          {saving ? 'Saving...' : 'Save Lead Scoring'}
        </Button>
        <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
};


// ─── Sub-components ──────────────────────────────────────────────

const SectionHeader = ({ title, icon: Icon, expanded, onToggle, testId }) => (
  <button
    onClick={onToggle}
    className="flex items-center gap-2 w-full text-left py-2 px-1 hover:bg-[hsl(var(--muted))] rounded transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]"
    aria-expanded={expanded}
    data-testid={testId}
  >
    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    <Icon className="h-4 w-4 text-[hsl(var(--muted-foreground))]" aria-hidden="true" />
    <span className="font-semibold text-sm">{title}</span>
  </button>
);

const NumberField = ({ id, label, value, onChange, testId, accent }) => (
  <div>
    <Label htmlFor={id} className="text-xs font-medium">{label}</Label>
    <Input
      id={id}
      type="number"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
      className="mt-0.5"
      data-testid={testId}
    />
    {accent && <span className={`text-xs font-medium mt-0.5 block ${accent}`}>{label.split(' ')[0]}</span>}
  </div>
);

const RuleCard = ({ rule, index, formFields, confirmationPolicy, onUpdate, onRemove }) => (
  <div
    className={`p-3 rounded-lg border space-y-2 ${rule.hard_disqualify ? 'border-red-300 bg-red-50/30' : 'border-[hsl(var(--border))]'}`}
    data-testid={`scoring-rule-${index}`}
  >
    {/* Row 1: field_id, operator, value_type */}
    <div className="flex gap-2">
      <div className="flex-1">
        <Label className="text-xs">Field ID</Label>
        <Input
          value={rule.field_id}
          onChange={(e) => onUpdate(index, 'field_id', e.target.value)}
          placeholder="e.g. entry.123456"
          className="mt-0.5 text-sm"
          data-testid={`rule-field-${index}`}
          list={`fields-${index}`}
          aria-label={`Rule ${index + 1} field ID`}
        />
        {formFields.length > 0 && (
          <datalist id={`fields-${index}`}>
            {formFields.map(f => <option key={f.id} value={f.id}>{f.text}</option>)}
          </datalist>
        )}
      </div>
      <div className="w-28">
        <Label className="text-xs">Operator</Label>
        <select
          value={rule.operator}
          onChange={(e) => onUpdate(index, 'operator', e.target.value)}
          className="w-full mt-0.5 px-2 py-2 border border-[hsl(var(--border))] rounded-md text-sm bg-[hsl(var(--background))]"
          data-testid={`rule-operator-${index}`}
          aria-label={`Rule ${index + 1} operator`}
        >
          {VALID_OPERATORS.filter(op => OPERATOR_TYPE_COMPAT[op]?.includes(rule.value_type)).map(op => (
            <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
          ))}
        </select>
      </div>
      <div className="w-24">
        <Label className="text-xs">Value Type</Label>
        <select
          value={rule.value_type}
          onChange={(e) => onUpdate(index, 'value_type', e.target.value)}
          className="w-full mt-0.5 px-2 py-2 border border-[hsl(var(--border))] rounded-md text-sm bg-[hsl(var(--background))]"
          data-testid={`rule-value-type-${index}`}
          aria-label={`Rule ${index + 1} value type`}
        >
          {VALID_VALUE_TYPES.map(vt => <option key={vt} value={vt}>{vt}</option>)}
        </select>
      </div>
    </div>

    {/* Row 2: value, points, label, signal_type */}
    <div className="flex gap-2">
      <div className="flex-1">
        <Label className="text-xs">Value</Label>
        <Input
          value={rule.value}
          onChange={(e) => onUpdate(index, 'value', e.target.value)}
          placeholder={rule.value_type === 'number' ? '0' : 'e.g. fintech'}
          className="mt-0.5 text-sm"
          data-testid={`rule-value-${index}`}
          aria-label={`Rule ${index + 1} value`}
        />
      </div>
      <div className="w-16">
        <Label className="text-xs">Points</Label>
        <Input
          type="number"
          value={rule.points}
          onChange={(e) => onUpdate(index, 'points', parseInt(e.target.value) || 0)}
          className="mt-0.5 text-sm"
          data-testid={`rule-points-${index}`}
          aria-label={`Rule ${index + 1} points`}
        />
      </div>
      <div className="flex-1">
        <Label className="text-xs">Label</Label>
        <Input
          value={rule.label}
          onChange={(e) => onUpdate(index, 'label', e.target.value)}
          placeholder="e.g. Enterprise"
          className="mt-0.5 text-sm"
          data-testid={`rule-label-${index}`}
          aria-label={`Rule ${index + 1} label`}
        />
      </div>
      <div className="w-28">
        <Label className="text-xs">Signal</Label>
        <select
          value={rule.signal_type}
          onChange={(e) => onUpdate(index, 'signal_type', e.target.value)}
          className="w-full mt-0.5 px-2 py-2 border border-[hsl(var(--border))] rounded-md text-sm bg-[hsl(var(--background))]"
          data-testid={`rule-signal-${index}`}
          aria-label={`Rule ${index + 1} signal type`}
        >
          {VALID_SIGNAL_TYPES.map(st => <option key={st} value={st}>{SIGNAL_TYPE_LABELS[st]}</option>)}
        </select>
      </div>
    </div>

    {/* Row 3: Toggles + optional fields */}
    <div className="flex items-center gap-4 flex-wrap pt-1 border-t border-[hsl(var(--border))]">
      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
        <input
          type="checkbox"
          checked={rule.hard_disqualify}
          onChange={(e) => onUpdate(index, 'hard_disqualify', e.target.checked)}
          className="rounded border-[hsl(var(--border))] w-3.5 h-3.5"
          data-testid={`rule-hard-disqualify-${index}`}
        />
        <span className={rule.hard_disqualify ? 'text-red-700 font-medium' : ''}>Hard Disqualify</span>
      </label>
      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
        <input
          type="checkbox"
          checked={rule.required_for_finalization}
          onChange={(e) => onUpdate(index, 'required_for_finalization', e.target.checked)}
          className="rounded border-[hsl(var(--border))] w-3.5 h-3.5"
          data-testid={`rule-required-${index}`}
        />
        Required for Finalization
      </label>
      <div className="flex items-center gap-1.5">
        <Label className="text-xs whitespace-nowrap">Policy Override:</Label>
        <select
          value={rule.confirmation_policy_override || ''}
          onChange={(e) => onUpdate(index, 'confirmation_policy_override', e.target.value || null)}
          className="px-1.5 py-1 border border-[hsl(var(--border))] rounded text-xs bg-[hsl(var(--background))]"
          data-testid={`rule-policy-override-${index}`}
          aria-label={`Rule ${index + 1} policy override`}
        >
          <option value="">None (use global: {confirmationPolicy})</option>
          {VALID_POLICIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <Label className="text-xs whitespace-nowrap">Expires (sec):</Label>
        <Input
          type="number"
          value={rule.expires_after_seconds ?? ''}
          onChange={(e) => onUpdate(index, 'expires_after_seconds', e.target.value ? Number(e.target.value) : null)}
          placeholder="—"
          className="w-20 text-xs py-1"
          data-testid={`rule-expires-${index}`}
          aria-label={`Rule ${index + 1} expiration seconds`}
        />
      </div>
      <div className="ml-auto">
        <Button size="sm" variant="ghost" onClick={() => onRemove(index)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0" data-testid={`remove-rule-${index}`} aria-label={`Remove rule ${index + 1}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  </div>
);

const ConflictRuleCard = ({ rule, index, formFields, onUpdate, onRemove, onAddCondition, onRemoveCondition, onUpdateCondition }) => (
  <div className="p-3 rounded-lg border border-orange-200 bg-orange-50/30 space-y-2" data-testid={`conflict-rule-${index}`}>
    <div className="flex gap-2 items-start">
      <div className="flex-1">
        <Label className="text-xs">Description</Label>
        <Input
          value={rule.description}
          onChange={(e) => onUpdate(index, 'description', e.target.value)}
          placeholder="e.g. Budget mismatch"
          className="mt-0.5 text-sm"
          data-testid={`conflict-desc-${index}`}
          aria-label={`Conflict rule ${index + 1} description`}
        />
      </div>
      <div className="w-32">
        <Label className="text-xs">Cap Label</Label>
        <select
          value={rule.cap_value}
          onChange={(e) => onUpdate(index, 'cap_value', e.target.value)}
          className="w-full mt-0.5 px-2 py-2 border border-[hsl(var(--border))] rounded-md text-sm bg-[hsl(var(--background))]"
          data-testid={`conflict-cap-${index}`}
          aria-label={`Conflict rule ${index + 1} cap value`}
        >
          {VALID_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      <div className="flex items-end">
        <Button size="sm" variant="ghost" onClick={() => onRemove(index)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0" data-testid={`remove-conflict-${index}`} aria-label={`Remove conflict rule ${index + 1}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[hsl(var(--muted-foreground))]">Conditions</span>
        <Button size="sm" variant="ghost" onClick={() => onAddCondition(index)} className="h-6 text-xs" data-testid={`add-condition-${index}`}>
          <Plus className="h-3 w-3 mr-0.5" /> Condition
        </Button>
      </div>
      {(rule.conditions || []).map((cond, ci) => (
        <div key={ci} className="flex gap-1.5 items-center" data-testid={`conflict-${index}-cond-${ci}`}>
          <Input
            value={cond.field_id}
            onChange={(e) => onUpdateCondition(index, ci, 'field_id', e.target.value)}
            placeholder="field_id"
            className="flex-1 text-xs py-1"
            list={`cfields-${index}-${ci}`}
            aria-label={`Condition ${ci + 1} field`}
          />
          {formFields.length > 0 && (
            <datalist id={`cfields-${index}-${ci}`}>
              {formFields.map(f => <option key={f.id} value={f.id} />)}
            </datalist>
          )}
          <select
            value={cond.operator}
            onChange={(e) => onUpdateCondition(index, ci, 'operator', e.target.value)}
            className="w-24 px-1.5 py-1 border border-[hsl(var(--border))] rounded text-xs bg-[hsl(var(--background))]"
            aria-label={`Condition ${ci + 1} operator`}
          >
            {VALID_OPERATORS.map(op => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
          </select>
          <Input
            value={cond.value}
            onChange={(e) => onUpdateCondition(index, ci, 'value', e.target.value)}
            placeholder="value"
            className="flex-1 text-xs py-1"
            aria-label={`Condition ${ci + 1} value`}
          />
          {(rule.conditions || []).length > 1 && (
            <Button size="sm" variant="ghost" onClick={() => onRemoveCondition(index, ci)} className="h-6 w-6 p-0 text-red-500" aria-label={`Remove condition ${ci + 1}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      ))}
    </div>
  </div>
);
